import { z } from 'zod';
import { integrationSchedulePutRequestSchema } from '@/lib/api/work-integrations/schedule';

export const syncDirectionSchema = z.enum(['PULL_ONLY', 'PUSH_ONLY', 'BIDIRECTIONAL']);
export type SyncDirection = z.infer<typeof syncDirectionSchema>;
export const SyncDirection = syncDirectionSchema.enum;

export const workIntegrationDto = z.object({
  externalIntegrationsDto: z
    .object({
      credentialsId: z.number().optional(),
      sourceSystem: z.string().optional(),
      sourceItemId: z.string().optional(),
      name: z.string().optional(),
      sourceFilter: z
        .object({
          itemTypes: z.array(z.string()).optional(),
          itemStatuses: z.array(z.string()).optional(),
          startDate: z.string().optional(),
          jql: z.string().optional(),
          customFieldFilters: z
            .array(
              z.object({
                id: z.string().optional(),
                fieldId: z.string().optional(),
                fieldType: z.enum(['SELECT', 'MULTI_SELECT', 'DATE', 'NUMERIC']).optional(),
                operator: z
                  .enum([
                    'EQUALS',
                    'NOT_EQUALS',
                    'IN',
                    'NOT_IN',
                    'IS_EMPTY',
                    'IS_NOT_EMPTY',
                    'GREATER_THAN',
                    'GREATER_THAN_OR_EQUALS',
                    'LESS_THAN',
                    'LESS_THAN_OR_EQUALS',
                    'BETWEEN',
                    'NOT_BETWEEN',
                  ])
                  .optional(),
                value: z.union([z.string(), z.number(), z.array(z.string()), z.null()]).optional(),
              }),
            )
            .optional(),
        })
        .optional(),
      sourceFields: z
        .object({
          estimate: z.string().optional(),
          team: z.string().optional(),
          expectedStartDate: z.string().optional(),
          expectedFinishDate: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  jiraAccessibleResourcesDto: z
    .object({
      cloudId: z.string().optional(),
      instanceUrl: z.string().optional(),
    })
    .optional(),
});

export const customFieldMappingRowSchema = z.object({
  tempoFieldId: z.number(),
  instanceFieldMappingId: z.number().optional(),
  syncDirection: syncDirectionSchema,
});

export type CustomFieldMappingRow = z.infer<typeof customFieldMappingRowSchema>;

export const autoSyncFormSchema = z.object({
  enabled: z.boolean(),
  direction: z.enum(['PULL', 'PUSH']),
  frequency: z.enum(['30m', '60m']),
});

export type AutoSyncFormValues = z.infer<typeof autoSyncFormSchema>;

export const jiraImportWizardFormSchema = workIntegrationDto.extend({
  itemDto: z
    .object({
      type: z.literal('items'),
      name: z.string().optional(),
      itemType: z.literal('custom').optional(),
      folderStatus: z.literal('active').optional(),
      sourceSystem: z.literal('jira').optional(),
    })
    .optional(),
  itemLevelMappings: z
    .array(
      z.object({
        itemLevel: z.number(),
        jiraLevel: z.number(),
      }),
    )
    .optional(),
  itemTypeMappings: z
    .array(
      z.object({
        itemTypeId: z.number(),
        issueTypeId: z.string(),
      }),
    )
    .optional(),
  customFieldMappings: z.array(customFieldMappingRowSchema).optional(),
  autoSync: autoSyncFormSchema.optional(),
});

export type JiraImportWizardFormSchema = z.infer<typeof jiraImportWizardFormSchema>;

export const jiraImportWizardSubmissionSchema = z.object({
  oauthCredentialsId: z.number(),
  instanceId: z.string(),
  instanceUrl: z.string(),
  sourceProjectId: z.string(),
  name: z.string(),
  loopId: z.number(),
  fieldMapping: z.object({
    estimateFieldId: z.string(),
    teamFieldId: z.string(),
    expectedStartDateFieldId: z.string().optional(),
    expectedFinishDateFieldId: z.string().optional(),
  }),
  customFieldMappings: z.array(
    z.object({
      instanceFieldMappingId: z.number(),
      syncDirection: syncDirectionSchema,
    }),
  ),
  filters: z.object({
    typeIds: z.array(z.string()).optional(),
    statusIds: z.array(z.string()).optional(),
    includeWorkAfter: z.string().optional(),
    jql: z.string().optional(),
    customFieldFilters: z
      .array(
        z.object({
          id: z.string().optional(),
          fieldId: z.string().optional(),
          fieldType: z.enum(['SELECT', 'MULTI_SELECT', 'DATE', 'NUMERIC']).optional(),
          operator: z
            .enum([
              'EQUALS',
              'NOT_EQUALS',
              'IN',
              'NOT_IN',
              'IS_EMPTY',
              'IS_NOT_EMPTY',
              'GREATER_THAN',
              'GREATER_THAN_OR_EQUALS',
              'LESS_THAN',
              'LESS_THAN_OR_EQUALS',
              'BETWEEN',
              'NOT_BETWEEN',
            ])
            .optional(),
          value: z.union([z.string(), z.number(), z.array(z.string()), z.null()]).optional(),
        }),
      )
      .optional(),
  }),
  itemLevelMappings: z
    .array(
      z.object({
        itemLevel: z.number(),
        jiraLevel: z.number(),
      }),
    )
    .optional(),
  itemTypeMappings: z
    .array(
      z.object({
        itemTypeId: z.number(),
        issueTypeId: z.string(),
      }),
    )
    .optional(),
  autoSync: integrationSchedulePutRequestSchema.optional(),
});

export type JiraImportWizardSubmissionSchema = z.infer<typeof jiraImportWizardSubmissionSchema>;
